/**
 * Menú "Generar video" (Animar imagen) — panel lateral en desktop, bottom sheet
 * con drag-to-dismiss en mobile — ADR-2026-09-16-los-menus-del-visor-son-componentes.
 *
 * Dos pasos: el clip de muestra ("Generar video") y la configuración ("Configurar
 * video": descripción sugerida editable, duración y escenas múltiples). Vivía
 * embebido en el visor del historial (y su gemelo en el inline); salió acá cuando el
 * lienzo del Agente necesitó EL MISMO menú sin el visor. Todo el estado es del host
 * (paso, prompt, duración, escenas): el host es quien pide la sugerencia al pasar de
 * paso y quien genera. El botón de generar lleva el costo con el ícono de créditos.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Info, Loader2, X } from 'lucide-react';
import { CreditBadge } from './CreditBadge';
import { palabraDeModeloActual } from '../lib/nombreDeModelo';

export type PasoVideoMenu = 'select' | 'prompt';

export interface VideoMenuProps {
  open: boolean;
  step: PasoVideoMenu;
  onStepChange: (step: PasoVideoMenu) => void;
  prompt: string;
  onPromptChange: (prompt: string) => void;
  /** La sugerencia está viajando: el campo se reemplaza por la barra de espera. */
  loadingPrompt: boolean;
  duration: number;
  onDurationChange: (duration: number) => void;
  multiScene: boolean;
  onMultiSceneChange: (multiScene: boolean) => void;
  /** Créditos que va a cobrar (duración, ×2 con escenas múltiples). */
  cost: number;
  onClose: () => void;
  /** "Siguiente" del primer paso: el host pasa al segundo y pide la sugerencia. */
  onNext: () => void;
  onGenerate: () => void;
  /** La imagen que se va a animar. En el teléfono encabeza la hoja, como en la pantalla
   *  de Animar (ADR-2026-10-28-animar-del-telefono-como-el-carrusel). */
  imagenUrl?: string | null;
}

const DURACION_MIN = 4;
const DURACION_MAX = 15;
const DURACION_MS = 300;
/** Píxeles de arrastre a partir de los cuales la hoja del teléfono se cierra. */
const DRAG_CIERRE_PX = 100;
/** El teléfono: la hoja (`sm:hidden`) en vez del panel lateral. */
const CONSULTA_TELEFONO = '(max-width: 639px)';

export const VideoMenu: React.FC<VideoMenuProps> = ({
  open,
  step,
  onStepChange,
  prompt,
  onPromptChange,
  loadingPrompt,
  duration,
  onDurationChange,
  multiScene,
  onMultiSceneChange,
  cost,
  onClose,
  onNext,
  onGenerate,
  imagenUrl,
}) => {
  const [montado, setMontado] = useState(false);
  const [animado, setAnimado] = useState(false);
  const [dragY, setDragY] = useState(0);
  const dragStartY = useRef<number | null>(null);

  useEffect(() => {
    if (open) {
      setDragY(0);
      setMontado(true);
      return;
    }
    setAnimado(false);
    const id = setTimeout(() => {
      setMontado(false);
      setDragY(0);
    }, DURACION_MS);
    return () => clearTimeout(id);
  }, [open]);
  useEffect(() => {
    if (!montado) return;
    const id = requestAnimationFrame(() => setAnimado(true));
    return () => cancelAnimationFrame(id);
  }, [montado]);

  // En el teléfono no hay paso del clip de muestra: la hoja abre directo en la
  // configuración, como la pantalla de Animar con la imagen ya elegida
  // (ADR-2026-10-28-animar-del-telefono-como-el-carrusel). `onNext` es el "Siguiente" de
  // siempre: el host pasa de paso y pide la sugerencia.
  const onNextRef = useRef(onNext);
  onNextRef.current = onNext;
  useEffect(() => {
    if (open && step === 'select' && window.matchMedia(CONSULTA_TELEFONO).matches) onNextRef.current();
  }, [open, step]);

  if (!montado) return null;

  const titulo = step === 'prompt' ? 'Configurar video' : 'Generar video';
  const volverOCerrar = step === 'prompt' ? () => onStepChange('select') : onClose;

  const muestra = (
    <div className="flex flex-col items-center">
      <video
        src="/images/video_animar_imagen.mp4"
        autoPlay
        loop
        muted
        playsInline
        className="w-4/5 mx-auto aspect-[4/5] object-cover rounded-[10px] mb-4"
      />
      <p className="text-[15px] text-neutral-400 text-center">
        Convertí tu imagen en un video animado con movimiento realista generado por IA.
      </p>
    </div>
  );

  const configuracion = (
    <>
      {/* Descripción del video — iOS grouped section */}
      <div>
        <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">
          Descripción del video
        </p>
        <div className="rounded-[13px] bg-white overflow-hidden border border-neutral-200">
          {loadingPrompt ? (
            <div className="px-4 py-4">
              <div className="flex items-center gap-2 mb-3">
                <Loader2 className="w-5 h-5 text-brand-primary animate-spin" />
                <span className="text-[15px] text-[#3C3C43]/60">Generando idea para el video...</span>
              </div>
              <div className="w-full h-1 bg-black/[0.06] rounded-full overflow-hidden">
                <div
                  className="h-full bg-brand-primary rounded-full"
                  style={{ animation: 'videoPromptProgress 15s linear forwards' }}
                />
              </div>
              <style>{`@keyframes videoPromptProgress { from { width: 0%; } to { width: 100%; } }`}</style>
            </div>
          ) : (
            <div className="px-4 py-3">
              <textarea
                rows={4}
                value={prompt}
                onChange={(e) => onPromptChange(e.target.value)}
                placeholder={`Ej: Que el ${palabraDeModeloActual().singular} camine hacia la cámara con movimiento suave...`}
                className="w-full text-[15px] text-brand-black/90 placeholder:text-[#3C3C43]/30 bg-transparent resize-none focus:outline-none leading-relaxed"
              />
            </div>
          )}
        </div>
        {!loadingPrompt && (
          <p className="text-[13px] text-[#3C3C43]/60 mt-2">Cuanto más específico seas, mejor será el resultado.</p>
        )}
      </div>

      {/* Configuración — iOS grouped section */}
      <div className="mt-6">
        <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">Configuración</p>
        <div className="rounded-[13px] bg-white overflow-hidden border border-neutral-200">
          {/* Duración — slider */}
          <div className="px-4 py-[11px]">
            <div className="flex items-center gap-3 mb-3">
              <span className="material-symbols-rounded text-brand-primary text-[20px] shrink-0">timer</span>
              <span className="text-[15px] text-brand-black/90">Duración</span>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex-1 flex flex-col">
                <div className="relative h-[22px] flex items-center">
                  <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[6px] rounded-full bg-neutral-200" />
                  <div
                    className="absolute top-1/2 -translate-y-1/2 left-0 h-[6px] rounded-full bg-brand-primary"
                    style={{ width: `${((duration - DURACION_MIN) / (DURACION_MAX - DURACION_MIN)) * 100}%` }}
                  />
                  <input
                    type="range"
                    min={DURACION_MIN}
                    max={DURACION_MAX}
                    step={1}
                    value={duration}
                    onChange={(e) => onDurationChange(Number(e.target.value))}
                    className="absolute inset-0 w-full appearance-none bg-transparent cursor-pointer z-10 m-0 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-[22px] [&::-webkit-slider-thumb]:h-[22px] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-[0_0.5px_4px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.04)] [&::-webkit-slider-thumb]:border-0 [&::-webkit-slider-runnable-track]:bg-transparent [&::-webkit-slider-runnable-track]:h-[22px]"
                  />
                </div>
                <div className="flex justify-between px-[2px] mt-1">
                  {Array.from({ length: DURACION_MAX - DURACION_MIN + 1 }, (_, i) => (
                    <div
                      key={i}
                      className={`w-[3px] h-[3px] rounded-full ${i + DURACION_MIN <= duration ? 'bg-[rgb(var(--brand-primary-rgb)_/_0.4)]' : 'bg-neutral-300'}`}
                    />
                  ))}
                </div>
              </div>
              <span className="text-[15px] font-medium text-brand-black/90 tabular-nums shrink-0 min-w-[58px] text-right">
                {duration} seg
              </span>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <Info className="w-3.5 h-3.5 text-brand-primary shrink-0" />
              <span className="text-[13px] text-[#3C3C43]/60">Se consumirán {cost} imágenes</span>
            </div>
          </div>

          <div className="ml-[46px] h-px bg-black/[0.08]" />

          {/* Múltiples escenas — toggle */}
          <div className="px-4 py-[11px]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="material-symbols-rounded text-brand-primary text-[20px] shrink-0">theaters</span>
                <span className="text-[15px] text-brand-black/90">Múltiples escenas</span>
              </div>
              <button
                type="button"
                onClick={() => onMultiSceneChange(!multiScene)}
                className={`relative w-[51px] h-[31px] rounded-full transition-colors duration-200 ${multiScene ? 'bg-brand-primary' : 'bg-neutral-300'}`}
              >
                <div
                  className={`absolute top-[2px] left-[2px] w-[27px] h-[27px] rounded-full bg-white shadow-[0_0.5px_4px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.04)] transition-transform duration-200 ${multiScene ? 'translate-x-[20px]' : 'translate-x-0'}`}
                />
              </button>
            </div>
            {multiScene && (
              <p className="text-[13px] text-[#3C3C43]/60 mt-2">
                El video se dividirá en varias escenas con transiciones automáticas para crear un resultado más
                dinámico y cinematográfico.
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(
    <>
      <div
        className={`fixed inset-0 z-[200] bg-black/40 transition-opacity duration-300 ${animado ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
      />
      {/* Desktop: side panel */}
      <div
        className={`hidden sm:flex fixed top-4 right-4 bottom-4 w-[420px] bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] z-[201] flex-col overflow-hidden transition-transform duration-300 ease-out ${animado ? 'translate-x-0' : 'translate-x-[calc(100%+32px)]'}`}
      >
        <div className="flex items-center justify-between px-5 py-4">
          <h3 className="text-[17px] font-semibold text-brand-black/90">{titulo}</h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="mx-5 h-px bg-black/[0.08]" />
        <div className={`flex-1 overflow-y-auto px-5 py-4${step === 'select' ? ' flex items-center justify-center' : ''}`}>
          {step === 'select' ? muestra : configuracion}
        </div>
        <div className="px-5 py-3 flex justify-end gap-3">
          <button
            type="button"
            onClick={volverOCerrar}
            className="text-[15px] text-[#3C3C43]/60 hover:text-brand-black/80 px-3 py-2 transition-colors"
          >
            {step === 'prompt' ? 'Volver' : 'Cancelar'}
          </button>
          {step === 'select' ? (
            <button
              type="button"
              onClick={onNext}
              className="text-[15px] font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded-md px-4 py-2 shadow-sm transition-colors"
            >
              Siguiente
            </button>
          ) : (
            <button
              type="button"
              onClick={onGenerate}
              className="inline-flex items-center gap-2 text-[15px] font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded-md px-4 py-2 shadow-sm transition-colors"
            >
              Generar video
              <CreditBadge credits={cost} tone="on-primary" size="sm" />
            </button>
          )}
        </div>
      </div>
      {/* Mobile: la hoja del Carrusel (título, X, Generar a lo ancho) con la imagen que se
          anima arriba y la configuración de la pantalla de Animar
          (ADR-2026-10-28-animar-del-telefono-como-el-carrusel). */}
      <div
        className="sm:hidden fixed inset-x-0 bottom-0 z-[201] bg-[#F2F2F7] rounded-t-[13px] flex flex-col"
        style={{
          maxHeight: 'calc(100dvh - 1rem - env(safe-area-inset-top, 0px))',
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
          transform: dragY > 0 ? `translateY(${dragY}px)` : animado ? 'translateY(0)' : 'translateY(100%)',
          // Durante el arrastre no hay transición: la hoja sigue el dedo cuadro a cuadro.
          transition: dragY > 0 ? 'none' : 'transform 300ms ease-out',
        }}
      >
        <div
          className="flex justify-center pt-2 pb-1 cursor-grab active:cursor-grabbing shrink-0"
          onTouchStart={(e) => {
            dragStartY.current = e.touches[0].clientY;
          }}
          onTouchMove={(e) => {
            if (dragStartY.current !== null) {
              const dy = e.touches[0].clientY - dragStartY.current;
              if (dy > 0) setDragY(dy);
            }
          }}
          onTouchEnd={() => {
            dragStartY.current = null;
            if (dragY > DRAG_CIERRE_PX) onClose();
            else setDragY(0);
          }}
        >
          <div className="w-9 h-[5px] rounded-full bg-neutral-300" />
        </div>
        <div className="flex items-center justify-between px-5 py-3 shrink-0">
          <h3 className="text-[15px] font-semibold text-brand-black/90">Animar imagen</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 active:bg-black/[0.1] transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="mx-5 h-px bg-black/[0.08] shrink-0" />
        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4">
          {imagenUrl && (
            <div className="flex justify-center mb-6">
              <img
                src={imagenUrl}
                alt="La imagen que se va a animar"
                className="max-h-[32dvh] w-auto max-w-full object-contain rounded-[13px]"
              />
            </div>
          )}
          {configuracion}
        </div>
        {/* Pie: sólo Generar, a lo ancho, como el del Carrusel; la X de arriba cierra. */}
        <div className="px-5 py-3 shrink-0">
          <button
            type="button"
            onClick={onGenerate}
            disabled={loadingPrompt}
            className="w-full inline-flex items-center justify-center gap-2 text-[15px] font-semibold text-white bg-brand-primary active:bg-brand-primary-hover disabled:opacity-40 rounded-md px-4 py-3 shadow-sm transition-colors"
          >
            Generar video
            <CreditBadge credits={cost} tone="on-primary" size="sm" />
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
};

export default VideoMenu;
